import React, { Suspense, useCallback, useEffect } from 'react';

import { isNil } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Route, Routes, useMatch } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { isFalse } from 'utils';

import AppHeader from 'components/app-header';
import AppflowUpdateProgress from 'components/appflow-update-progress';
import { AutoSelectRestaurant } from 'components/auto-select-restaurant';
import { BottomServiceMode } from 'components/bottom-service-mode';
import ErrorNetwork from 'components/error-network';
import Footer from 'components/footer';
import LanguageSelectorModal from 'components/language-selector-modal';
import { regionFilter } from 'components/language-selector/regionFilter';
import LoadingAnimation from 'components/loading-animation';
import MobileNavigation from 'components/mobile-web-navigation-bar';
import SetLoading from 'components/set-loading';
import SiteUpdating from 'components/site-updating';
import { ToastContainer } from 'components/toast';
import WelcomeMessage from 'components/welcome-message';
import {
  useAboutContentQuery,
  useFeatureAccountQuery,
  useMainMenuContentQuery,
  usePromotionsContentQuery,
  useSignupContentQuery,
  useStaticPagesContentQuery
} from 'generated/sanity-graphql';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useResetStoreResetCartResetServiceModeTimeout } from 'hooks/reset-store-timeout';
import { useAppHeaderDimensions } from 'hooks/use-app-header-dimensions';
import { useAppflow } from 'hooks/use-appflow';
import useCoordinatedAppDataQueries from 'hooks/use-coordinated-app-data-queries';
import useDatadogRUM from 'hooks/use-datadog-rum';
import useEffectOnce from 'hooks/use-effect-once';
import useGoogleAnalytics from 'hooks/use-google-analytics';
import useMediaQuery from 'hooks/use-media-query';
import { useOffersDataset } from 'hooks/use-offers-dataset';
import { usePrevious } from 'hooks/use-previous';
import { useSetUserLocale } from 'hooks/use-set-user-locale';
import homePageAboveTheFold from 'pages/home/above-the-fold';
import LoyaltyOptInModal from 'pages/loyalty/loyalty-opt-in-modal';
import { useCustomizableContent } from 'state/customizable-content';
import { useFeaturesContext } from 'state/features';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { NEXT_URL_QUERY_PARAM } from 'state/location/constants';
import { useLogger } from 'state/logger';
import { useNetworkContext } from 'state/network';
import { useOrderContext } from 'state/order';
import { usePageManagerContext } from 'state/static-page-manager';
import { useUIContext } from 'state/ui';
import { fullBrandName } from 'utils/environment';
import { routes } from 'utils/routing';

import brandRoutes from './brand-routes';
import { theme } from './brand-theme';
import { DefaultCookieBannerContainer, OneTrustCookieBanner } from './cookie-banner-container';
import { scrollTo } from './scroll-to';
import useLogPageView from './use-log-page-view';
import useRobotMetaTags from './use-robot-meta-tags';
import useScrollTop from './use-scroll-top';
import { LazyRoute, lazyWithFallback } from './util';

const Cart: LazyRoute = lazyWithFallback(() => import('pages/cart'));
const MainMenu: LazyRoute = lazyWithFallback(() => import('pages/main-menu'));
const MenuContent: LazyRoute = lazyWithFallback(() => import('pages/menu-content'));
const Home: LazyRoute = lazyWithFallback(() => import('pages/home'));
const Account: LazyRoute = lazyWithFallback(() => import('pages/account'));
const CommPreferences: LazyRoute = lazyWithFallback(() => import('pages/account/comm-preferences'));
const AccountInfo: LazyRoute = lazyWithFallback(() => import('pages/account/account-info'));
const AccountDelete: LazyRoute = lazyWithFallback(() => import('pages/account/account-delete'));
const AccountRequestInfo: LazyRoute = lazyWithFallback(() =>
  import('pages/account/account-request-info')
);
const PaymentMethods: LazyRoute = lazyWithFallback(() => import('pages/account/payment-methods'));
const Diagnostics: LazyRoute = lazyWithFallback(() => import('pages/diagnostics'));
const AccountOrders: LazyRoute = lazyWithFallback(() => import('pages/account/account-orders'));
const Offers: LazyRoute = lazyWithFallback(() => import('pages/offers'));
const LoyaltyHome: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-home'));
const SignUp: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-up'));
const SignIn: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-in'));
const Quiz: LazyRoute = lazyWithFallback(() => import('components/quiz'));
const LocationPromo: LazyRoute = lazyWithFallback(() => import('pages/halloween-promo'));
const OnboardingModal: LazyRoute = lazyWithFallback(() =>
  import('pages/account/tims-rewards/onboarding-modal')
);

const AuthChallengeScreenJwt: LazyRoute = lazyWithFallback(() =>
  import('pages/authentication/auth-email-jwt')
);
const ConfirmJwt: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-jwt'));
const ConfirmOtp: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-otp'));
const SignOut: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-out'));
const StoreLocator: LazyRoute = lazyWithFallback(() => import('pages/store-locator'));
const OrderConfirmation: LazyRoute = lazyWithFallback(() =>
  import('pages/order-confirmation/order-confirmation')
);
const StoreOrderConfirmation: LazyRoute = lazyWithFallback(() =>
  import('pages/order-confirmation/store/store-order-confirmation')
);
const About: LazyRoute = lazyWithFallback(() => import('pages/about'));
const StaticPage: LazyRoute = lazyWithFallback(() => import('pages/static'));
const NotFound: LazyRoute = lazyWithFallback(() => import('pages/not-found'));
const SupportPage: LazyRoute = lazyWithFallback(() => import('pages/support'));
const SupportV2CategoriesPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-categories-page')
);
const SupportV2CategoryPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-category-page')
);
const SupportV2FormPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-form-page')
);
const SupportV2OrdersListPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-orders-list-page')
);
const ReportOrderIssuePage: LazyRoute = lazyWithFallback(() => import('pages/report-order-issue'));
const ReportOrderNeverArrivedPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-never-arrived')
);
const ReportOrderItemsMissingPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-items-missing')
);
const OfferActivatePage: LazyRoute = lazyWithFallback(() => import('pages/offer-activate'));
const SendGiftPage: LazyRoute = lazyWithFallback(() => import('pages/send-gift'));
const AcceptGift = lazyWithFallback(() => import('pages/accept-gift'));

const LayoutContainer = styled.div<{ $bottomInset: number }>`
  background: ${Styles.color.background};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-x: hidden;
  width: 100%;
  padding-bottom: calc(env(safe-area-inset-bottom) + ${p => p.$bottomInset}px);
  &:focus {
    top: 0.2rem;
    left: 0.2rem;
    opacity: 1;
  }
  min-height: calc(100vh - ${p => p.$bottomInset}px);
  ${Styles.desktop`
    min-height: 100vh;
    #router-wrapper {
      min-height: 100vh; /* Fixes div collapse bug DON'T REMOVE */
    }
  `}

  ${Styles.mobileFullscreen`
    position: relative;
    margin-top: env(safe-area-inset-top); //Not 100% sure this works on android
  `}
`;

type ContentContainerProps = {
  $topInset: number;
  $bottomInset: number;
};

const ContentContainer = styled.div<ContentContainerProps>`
  background: ${Styles.color.background};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  ${Styles.mobile`
    // add padding equivalent to the height of the header minus
    // the safe area view (already accounted for in LayoutContainer's margin-top)
    padding-top: ${(p: ContentContainerProps) =>
      `calc(${p.$topInset}px - env(safe-area-inset-top))`};
    min-height: ${(p: ContentContainerProps) =>
      `calc(100vh - ${p.$topInset}px - env(safe-area-inset-top) - ${p.$bottomInset}px)`};
  `}
`;

const ContentLoadingContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
`;

interface IRouterWrapperProps {
  $topInset: number;
  $bottomInset: number;
}
const RouterWrapper = styled.div<IRouterWrapperProps>`
  background: ${Styles.color.background};
  flex-grow: 2;
  min-height: ${({ $topInset, $bottomInset }) =>
    `calc(100vh - ${$topInset}px - env(safe-area-inset-top) - ${$bottomInset}px)`};
`;

const ContentRouting = () => {
  useGoogleAnalytics();
  const { clearServerOrder } = useOrderContext(); // @todo type order context
  const { appHeaderDimensions } = useAppHeaderDimensions();
  const { appFooterDimensions, bottomServiceDimensions } = useUIContext();

  const { formatMessage } = useIntl();

  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);
  const enableAccountDeletePage = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_DELETE_PAGE);

  const enableRequestMyInformation = useFlag(LaunchDarklyFlag.ENABLE_REQUEST_MY_INFORMATION);
  const enableCommPrefFlag = useFlag(LaunchDarklyFlag.ENABLE_COMMUNICATION_PREFERENCES);
  const enableCommunicationPreferences = !isFalse(enableCommPrefFlag);

  const enableInAppQuizGame = useFlag(LaunchDarklyFlag.ENABLE_IN_APP_QUIZ_GAME);
  const halloweenBkPromoLocations = useFlag(LaunchDarklyFlag.HALLOWEEN_BK_PROMO_LOCATIONS);
  const enableHalloweenLocationsPromo =
    halloweenBkPromoLocations && Object.keys(halloweenBkPromoLocations).length;

  const enableOfferGifting = useFlag(LaunchDarklyFlag.ENABLE_OFFER_GIFTING);

  const enableGuestAndMultiStepSupportForm = useFlag(
    LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
  );

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;

  const forceAuthOnHomepageFlag = useFlag(LaunchDarklyFlag.FORCE_HOMEPAGE_AUTH);
  const isBaseRoute = useMatch(routes.base);

  const forceAuthOnHomepage = forceAuthOnHomepageFlag && enableSignUp && isBaseRoute;

  useAuthRedirects({ skip: !forceAuthOnHomepage });

  useEffect(() => {
    // reset the server order when a user navigate into the menu no matter what
    clearServerOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Base Routes */}
      <ContentContainer
        $topInset={appHeaderDimensions.height}
        $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
      >
        <RouterWrapper
          $topInset={appHeaderDimensions.height}
          $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
        >
          <Routes>
            <Route path={'*'} element={<StaticPage />} />
            <Route path={routes.notFound404} element={<NotFound />} />
            <Route path={routes.confirmJwt} element={<ConfirmJwt />} />
            <Route path={routes.confirmOtp} element={<ConfirmOtp />} />
            {enableSignUp && (
              <>
                <Route path={routes.signUp} element={<SignUp />} />
                <Route path={formatMessage({ id: 'routes.signUp' })} element={<SignUp />} />
                <Route path={routes.signIn} element={<SignIn />} />
                <Route path={formatMessage({ id: 'routes.signIn' })} element={<SignIn />} />
                <Route path={routes.account} element={<Account />} />
                <Route path={formatMessage({ id: 'routes.account' })} element={<Account />} />
              </>
            )}
            <Route path={routes.authChallengeJwt} element={<AuthChallengeScreenJwt />} />
            <Route path={routes.signOut} element={<SignOut />} />
            <Route path={routes.menu} element={<MainMenu />} />
            <Route path={`${routes.menu}/:id`} element={<MenuContent />} />
            <Route path={routes.base} element={<Home />} />
            <Route path={`${routes.offers}/*`} element={<Offers />} />
            <Route
              path={`${formatMessage({ id: 'routes.loyalty' })}/*`}
              element={<LoyaltyHome />}
            />
            <Route path={routes.info} element={<AccountInfo />} />
            {enableCommunicationPreferences && (
              <Route path={routes.communication} element={<CommPreferences />} />
            )}
            <Route path={`${routes.orders}/*`} element={<AccountOrders />} />
            {enableAccountDeletePage && (
              <Route
                path={formatMessage({ id: 'routes.accountDelete' })}
                element={<AccountDelete />}
              />
            )}
            {enableRequestMyInformation && (
              <Route
                path={formatMessage({ id: 'routes.accountRequestInfo' })}
                element={<AccountRequestInfo />}
              />
            )}
            {enableInAppQuizGame && <Route path={routes.quiz} element={<Quiz />} />}
            {enableInAppQuizGame && (
              <Route path={formatMessage({ id: 'routes.quiz' })} element={<Quiz />} />
            )}
            {enableHalloweenLocationsPromo && (
              <Route path={routes.halloweenPromo} element={<LocationPromo />} />
            )}
            <Route path={`${routes.payment}/*`} element={<PaymentMethods />} />
            <Route path={routes.rewardsOnboarding} element={<OnboardingModal />} />
            <Route
              path={formatMessage({ id: 'routes.rewardsOnboarding' })}
              element={<OnboardingModal />}
            />
            <Route path={routes.about} element={<About />} />
            <Route path={`${routes.diagnostics}/*`} element={<Diagnostics />} />
            {enableOrderSupport && (
              <Route
                path={`${routes.reportOrderIssue}/:orderId`}
                element={<ReportOrderIssuePage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingOrder}/:orderId`}
                element={<ReportOrderNeverArrivedPage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingItems}/:orderId`}
                element={<ReportOrderItemsMissingPage />}
              />
            )}
            {enableOfferGifting && (
              <>
                <Route path={formatMessage({ id: 'routes.sendGift' })} element={<SendGiftPage />} />
                <Route path={`${routes.acceptGift}/:token`} element={<AcceptGift />} />
              </>
            )}
            <Route
              path={`${routes.offerActivate}/:tokenId/:offerId`}
              element={<OfferActivatePage />}
            />
            {enableGuestAndMultiStepSupportForm ? (
              // general support requests
              <>
                {/* // general support requests */}
                <Route path={routes.supportForm} element={<SupportV2FormPage />} />
                <Route path={routes.supportOrders} element={<SupportV2OrdersListPage />} />
                <Route path={routes.supportCategories} element={<SupportV2CategoriesPage />} />
                <Route path={routes.supportCategory} element={<SupportV2CategoryPage />} />
                <Route path={routes.support} element={<SupportV2CategoriesPage />} />
              </>
            ) : (
              <Route path={routes.support} element={<SupportPage />} />
            )}

            {/* We only want brand-specific routes to be available for those brands */}
            {/* `brandRoutes` can be thought of a Map of routes (string) to Components (React component) */}
            {Object.entries(brandRoutes).map(([path, Component]) =>
              // if a route isn't catering we want it. if it is catering, we leave it up to the LD flag
              path !== routes.catering || enableCatering ? (
                <Route path={formatMessage({ id: path })} element={<Component key={path} />} />
              ) : null
            )}
          </Routes>
        </RouterWrapper>
        <Footer />
      </ContentContainer>
    </>
  );
};

const Layout = () => {
  const {
    appDataId,
    isAppDataLoading,
    hasBlockingCustomizableContentError
  } = useCustomizableContent();
  const {
    loading: isHomePageAboveTheFoldLoading,
    error: homePageAboveTheFoldHasError
  } = useCoordinatedAppDataQueries(homePageAboveTheFold, appDataId);

  const {
    location: { pathname },
    navigate
  } = useLocationContext();

  const { featureAccountId = '' } = useFeaturesContext();
  const { appFooterDimensions, bottomServiceDimensions } = useUIContext();
  const { loading: isFeatureAccountLoading } = useFeatureAccountQuery({
    variables: { featureAccountId },
    skip: !featureAccountId || pathname !== routes.communication || pathname !== routes.orders
  });

  const { loading: isSortedOffersLoading } = useOffersDataset();
  const { loading: isAboutPageLoading } = useAboutContentQuery({
    variables: { appDataId },
    skip: !appDataId || pathname !== routes.about
  });
  const { loading: isMainMenuContentLoading } = useMainMenuContentQuery({
    variables: { appDataId },
    skip: !appDataId || pathname !== routes.menu
  });
  const { loading: isSignUpContentLoading } = useSignupContentQuery({
    variables: { appDataId },
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-and-network',
    skip: !appDataId || pathname !== routes.signUp
  });

  const { loading: isStaticContentLoading } = useStaticPagesContentQuery({
    variables: { appDataId },
    skip: !appDataId
  });
  const { loading: isPromotionsContentLoading } = usePromotionsContentQuery({
    variables: { appDataId },
    skip: !appDataId || pathname !== routes.trending
  });

  useDatadogRUM();
  useLogPageView();
  useScrollTop();
  useSetUserLocale();
  const {
    setShowLanguageSelectorModal,
    setHasShownLocaleSelector,
    showLanguageSelectorModal,
    language
  } = useLocale();
  const isMobile = useMediaQuery('mobile');
  const { formatMessage } = useIntl();
  const logger = useLogger();
  const { hasNetworkError } = useNetworkContext();
  const { routesLoading } = usePageManagerContext();
  const showSiteUpdating = useFlag(LaunchDarklyFlag.SHOW_SITE_UPDATING);
  // Prompts user for location services at start of session and selects nearest store
  const enableAutoSelectRestaurant = useFlag(LaunchDarklyFlag.ENABLE_AUTO_SELECT_RESTAURANT);
  const geoLocationAvailable = navigator.geolocation;

  const enableCookieBanner = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_BANNER);
  const enableOneTrustCookieBanner = useFlag(
    LaunchDarklyFlag.ENABLE_ONE_TRUST_COOKIE_CONSENT_BANNER
  );

  const forceBlockingUpdate = new URL(window.location.href).searchParams.get('forceBlockingUpdate');

  const { error: appflowError, extractingUpdate, needsBlockingUpdate, progress } = useAppflow({
    logger,
    forceBlockingUpdate: !!forceBlockingUpdate
  });

  const handleModalDismiss = () => {
    setHasShownLocaleSelector();
    setShowLanguageSelectorModal(false);
  };

  let content;

  const prevAmount = usePrevious(showSiteUpdating);

  // setup bot noindex nofollow meta tags to required routes
  // See hook and CBA-243 for more details
  const robotMetaTags = useRobotMetaTags(pathname);

  useEffectOnce(() => {
    const nextLocationQueryStringParam = new URL(window.location.href).searchParams.get(
      NEXT_URL_QUERY_PARAM
    );

    if (nextLocationQueryStringParam) {
      const decodedNexRoute = decodeURIComponent(nextLocationQueryStringParam);

      // Check for relative path but do not accept schemaless urls to avoid open redirect vulnerability
      if (/^\/[^/]/.test(decodedNexRoute)) {
        navigate(decodedNexRoute, { replace: true });
      }
    }
  });

  useResetStoreResetCartResetServiceModeTimeout();

  useEffect(() => {
    if (prevAmount !== showSiteUpdating) {
      // reload the page if we are changing from true to false.
      // That means a new site was loaded
      if (prevAmount === true && showSiteUpdating === false) {
        window && window.location && window.location.reload();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevAmount]);

  const scrollToTop = useCallback(() => scrollTo({ top: 0 }), []);

  if (showSiteUpdating) {
    return (
      <ThemeProvider theme={theme}>
        <SiteUpdating />
      </ThemeProvider>
    );
  }

  if (needsBlockingUpdate && !hasNetworkError) {
    return (
      <ThemeProvider theme={theme}>
        <AppflowUpdateProgress
          extractingUpdate={extractingUpdate}
          hasError={!!appflowError}
          progress={progress}
        />
      </ThemeProvider>
    );
  }

  const blockingLoadingStates = (path: string) => {
    // The ultimate goal here is to preload what the critical pages will use,
    // without holding up the user waiting for content that isn't needed on the page.
    // As we extend this pattern, more routes should be added to this switch statement.
    // This should become the only "full page spinner" in the app, so we know it when we see it.
    switch (path) {
      case routes.base:
        return [isAppDataLoading, isHomePageAboveTheFoldLoading];
      case routes.offers:
        return [isAppDataLoading, isSortedOffersLoading];
      case routes.about:
        return [isAppDataLoading, isAboutPageLoading];
      case routes.orders:
      case routes.communication:
        return [isAppDataLoading, isFeatureAccountLoading];
      case routes.menu:
        return [isAppDataLoading, isMainMenuContentLoading];
      case routes.signUp:
        return [isAppDataLoading, isSignUpContentLoading];
      case routes.trending:
        return [isAppDataLoading, isPromotionsContentLoading];
      case routes.storeOrderConfirmation:
        return [routesLoading, isAppDataLoading];
      default:
        return [routesLoading, isAppDataLoading, isStaticContentLoading];
    }
  };

  if (blockingLoadingStates(pathname).some(queryIsLoading => !!queryIsLoading)) {
    content = (
      <ContentLoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </ContentLoadingContainer>
    );
  } else if (
    hasNetworkError ||
    hasBlockingCustomizableContentError ||
    homePageAboveTheFoldHasError
  ) {
    content = <ErrorNetwork />;
  } else {
    content = (
      <Suspense fallback={<SetLoading />}>
        <Routes>
          {/* Full page routes */}
          <Route path={`${routes.cart}/*`} element={<Cart />} />
          <Route path={routes.storeOrderConfirmation} element={<StoreOrderConfirmation />} />
          <Route path={`${routes.orderConfirmation}/:rbiOrderId`} element={<OrderConfirmation />} />
          <Route path={`${routes.storeLocator}/*`} element={<StoreLocator />} />
          <Route
            path={`${formatMessage({ id: 'routes.storeLocator' })}/*`}
            element={<StoreLocator />}
          />
          {/* Regular routes (with normal nav, header, footer, etc) */}
          <Route path="*" element={<ContentRouting />} />
        </Routes>
      </Suspense>
    );
  }

  const getToastBottomInset = () => {
    // Some routes on mobile did not have a bottom navigation nor bottom service bar so we need set the toast's bottomInset to be 0px
    const mobileRoutesWithoutBottomNavbar = [routes.cart];
    if (mobileRoutesWithoutBottomNavbar.includes(pathname)) {
      return '0px';
    }
    return `${(isMobile ? appFooterDimensions.height : 0) + bottomServiceDimensions.height}px`;
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet titleTemplate={`${fullBrandName()} - %s`} defaultTitle={fullBrandName()}>
        <html lang={language} />
      </Helmet>
      <ToastContainer bottomInset={getToastBottomInset()} />
      {robotMetaTags}

      <AppHeader />
      <LayoutContainer
        role="main"
        id="main"
        $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
      >
        {content}
      </LayoutContainer>
      <LoyaltyOptInModal />
      <BottomServiceMode />
      <MobileNavigation scrollToTop={scrollToTop} />
      {showLanguageSelectorModal && (
        <LanguageSelectorModal regionFilter={regionFilter} onModalDismiss={handleModalDismiss} />
      )}
      {enableAutoSelectRestaurant && geoLocationAvailable && <AutoSelectRestaurant />}
      {enableCookieBanner && !enableOneTrustCookieBanner && <DefaultCookieBannerContainer />}
      {enableOneTrustCookieBanner && <OneTrustCookieBanner />}
      <WelcomeMessage />
    </ThemeProvider>
  );
};

export default Layout;
